import {Injectable} from '@angular/core';
import {doc, Firestore, runTransaction, serverTimestamp} from '@angular/fire/firestore';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {
  BARCODE_SCANNER_USAGE_UPDATED_AT_FIELD,
  decideBarcodeScannerUsageWrite,
} from '../utils/dynamsoft-licence-usage.utils';

export const DYNAMSOFT_STORE_USER_CAP = 5;
export const DYNAMSOFT_STORE_CAP_ERROR_CODE = 'DYNAMSOFT_STORE_USER_CAP_REACHED';
export const DYNAMSOFT_STORE_USAGE_UNAVAILABLE_ERROR_CODE = 'DYNAMSOFT_STORE_USAGE_UNAVAILABLE';

/**
 * Records one scanner use per store per SAST day and blocks the sixth distinct user.
 * Writes follow the CL-71 document: email keys and `updatedAt`, both `serverTimestamp()`.
 */
@Injectable({
  providedIn: 'root',
})
export class DynamsoftLicenceUsageCapService {
  constructor(private readonly firestore: Firestore) {
  }

  /**
   * Reserves today's slot for this user, or throws before the caller initialises Dynamsoft.
   * Missing store, email, or a failed read/write blocks scanning. It does not allow unlimited use.
   *
   * @param storeId - Selected store.
   * @param email - Signed-in user's email. Dots are valid Firestore field names on `set`.
   */
  async registerOrThrow(storeId: string, email: string): Promise<void> {
    const normalisedEmail = email?.trim() ?? '';
    if (!storeId || !normalisedEmail) {
      throw this.usageUnavailableError('A store and user email are required before the scanner can start.');
    }

    const usagePath = createSharedPaths().setStoreId(storeId).barcodeScannerUsage();
    const usageRef = doc(this.firestore, usagePath);

    try {
      await runTransaction(this.firestore, async (transaction) => {
        const snapshot = await transaction.get(usageRef);
        const currentData = snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : undefined;
        const decision = decideBarcodeScannerUsageWrite(currentData, normalisedEmail, Date.now(), DYNAMSOFT_STORE_USER_CAP);

        if (decision === 'blocked') {
          throw {
            code: DYNAMSOFT_STORE_CAP_ERROR_CODE,
            message: `Daily barcode scanner limit reached for store ${storeId}`,
          };
        }

        const ownUse = {
          [BARCODE_SCANNER_USAGE_UPDATED_AT_FIELD]: serverTimestamp(),
          [normalisedEmail]: serverTimestamp(),
        };

        if (decision === 'overwrite') {
          transaction.set(usageRef, ownUse);
          return;
        }

        transaction.set(usageRef, ownUse, {merge: true});
      });
    } catch (error: unknown) {
      if (this.isStoreCapError(error)) {
        throw error;
      }
      throw this.usageUnavailableError('The store scanner usage record could not be read or written.');
    }
  }

  private isStoreCapError(error: unknown): boolean {
    return typeof error === 'object' && error !== null && 'code' in error
      && (error as {code?: unknown}).code === DYNAMSOFT_STORE_CAP_ERROR_CODE;
  }

  private usageUnavailableError(message: string): {code: string; message: string} {
    return {
      code: DYNAMSOFT_STORE_USAGE_UNAVAILABLE_ERROR_CODE,
      message,
    };
  }
}
